import {useColorTheme} from './color-theme.jsx';
import { useMemo, useState, useRef, useEffect } from 'react';
import { PALETTE, HEAT_RAMP, ChartCard, DeltaChip, NEUTRAL, SEMANTIC } from './shared.jsx';
import { brand } from '../brand.js';
import {
  EntranceGroup,
  MOTION,
  lineDataSig,
  useEntrance,
  useGrowCycle,
  usePrefersReducedMotion,
} from './motion.jsx';
import {ProgressRing} from './charts.jsx';
import {Callout,MeterBar} from './scorecard.jsx';
import { requireStrings } from './required-strings.js';

// Same contract as `strings` (DEC-10): the caller says which language the
// month / weekday names and numbers are in. No browser-locale default.
function requireLocale(component, locale) {
  if (typeof locale !== 'string' || locale.length === 0) {
    throw new Error(`${component}: the "locale" prop is required (e.g. "es", "en")`);
  }
  return locale;
}

// ── Calendar Heatmap (GitHub-style) ──────────────────────
// Month and weekday names come from Intl in the caller's locale (DEC-10);
// they used to be fixed English ("Jan", "Mo", "Less", "More").
function monthNamesShort(locale) {
  const fmt = new Intl.DateTimeFormat(locale, { month: 'short' });
  return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(2024, m, 1)));
}
// Rows are Date#getDay() order (0 = Sunday). Only Mon / Wed / Fri are
// labelled, as before. 2023-12-31 was a Sunday.
function weekdayLabels(locale) {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return Array.from({ length: 7 }, (_, dow) => (dow % 2 === 1 ? fmt.format(new Date(2023, 11, 31 + dow)) : ''));
}
const CALENDAR_STRING_KEYS = ['less', 'more'];

export function CalendarHeatmap({ data, year, colorScale, domain, height = 140, entrance = true, locale, strings, interactive = true, formatValue }) {
  const clientTheme = useColorTheme('CalendarHeatmap');
  if (clientTheme.override) colorScale = clientTheme.heatScale;
  const s = requireStrings('CalendarHeatmap', strings, CALENDAR_STRING_KEYS);
  const loc = requireLocale('CalendarHeatmap', locale);
  const [selected,setSelected] = useState(null);
  const [focusDay,setFocusDay] = useState(0);
  const monthNames = useMemo(() => monthNamesShort(loc), [loc]);
  const dayNames = useMemo(() => weekdayLabels(loc), [loc]);
  const cellsDrawn = useGrowCycle(useMemo(() => lineDataSig([year, data]), [year, data]), !entrance);
  const colors = colorScale?.length ? colorScale : clientTheme.heatScale;
  const finite = Object.values(data).filter(v=>typeof v==='number'&&Number.isFinite(v));
  const [minVal,maxVal] = domain || [0,Math.max(...finite,1)];
  const noData = s.noData || '—';
  const startDate = new Date(year,0,1), startDay = startDate.getDay();
  const days = Math.round((Date.UTC(year+1,0,1)-Date.UTC(year,0,1))/86400000);
  const weeks = Math.ceil((days+startDay)/7);
  const w = 40+weeks*13, h = 140;
  const dayInfo = (offset) => {
    const date = new Date(year,0,1+offset);
    const key = `${year}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    const value = data[key], valid = typeof value==='number'&&Number.isFinite(value);
    const label = date.toLocaleDateString(loc,{day:'numeric',month:'long',year:'numeric'});
    return {key,value,valid,label,text:valid?(formatValue?formatValue(value):value.toLocaleString(loc)):noData};
  };
  const inspect = selected==null?null:dayInfo(Math.min(selected,days-1));
  return <div data-calendar-heatmap="" data-interactive={interactive}>
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{minWidth:600,height}} aria-label={String(year)}>
        {monthNames.map((m,mi)=>{
          const offset = Math.round((Date.UTC(year,mi,1)-Date.UTC(year,0,1))/86400000);
          return <text key={mi} x={20+Math.floor((offset+startDay)/7)*13} y={10} fontSize="11" fill={NEUTRAL.faint}>{m}</text>;
        })}
        {dayNames.map((d,i)=><text key={i} x={2} y={24+i*13} fontSize="11" fill={NEUTRAL.faint}>{d}</text>)}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={cellsDrawn}>
          {Array.from({length:days},(_,offset)=>{
            const info=dayInfo(offset), i=offset+startDay;
            const t=maxVal===minVal?.5:(info.value-minVal)/(maxVal-minVal);
            const fill=info.valid?colors[Math.min(colors.length-1,Math.max(0,Math.floor(t*colors.length)))]: 'white';
            return <rect key={info.key} data-date={info.key} data-missing={!info.valid||undefined}
              x={20+Math.floor(i/7)*13} y={16+(i%7)*13} width="11" height="11" rx="2"
              fill={fill} stroke={selected===offset?brand.ink:info.valid?'none':NEUTRAL.axis}
              strokeDasharray={info.valid?undefined:'2 1'} strokeWidth={selected===offset?1.5:.7}
              role={interactive?'button':undefined} aria-label={`${info.label}: ${info.text}`}
              className={interactive?'cursor-pointer outline-none focus:stroke-navy focus:stroke-2':undefined}
              tabIndex={interactive ? (focusDay===offset?0:-1) : undefined}
              onMouseEnter={interactive?()=>setSelected(offset):undefined}
              onFocus={interactive?()=>{setFocusDay(offset);setSelected(offset)}:undefined}
              onClick={interactive?()=>{setFocusDay(offset);setSelected(offset)}:undefined}
              onKeyDown={interactive?e=>{
                const shifts={ArrowRight:7,ArrowLeft:-7,ArrowDown:1,ArrowUp:-1};
                let next=e.key==='Home'?0:e.key==='End'?days-1:shifts[e.key]!=null?Math.max(0,Math.min(days-1,offset+shifts[e.key])):null;
                if(next!=null){e.preventDefault();setFocusDay(next);setSelected(next);e.currentTarget.closest('svg').querySelector(`[data-date="${dayInfo(next).key}"]`)?.focus();}
                if(e.key==='Enter'||e.key===' '){e.preventDefault();setSelected(offset);}
              }:undefined}>
              <title>{info.label}: {info.text}</title>
            </rect>;
          })}
        </EntranceGroup>
        <text x={20} y={h-8} fontSize="11" fill={NEUTRAL.faint}>{noData} · ╌</text>
        <text x={w-46-colors.length*14} y={h-8} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{s.less}</text>
        {colors.map((c,i)=><rect key={i} x={w-40-(colors.length-i)*14} y={h-18} width="11" height="11" rx="2" fill={c}/>)}
        <text x={w-36} y={h-8} fontSize="11" fill={NEUTRAL.faint}>{s.more}</text>
      </svg>
    </div>
    {interactive&&<div data-calendar-readout="" aria-live="polite" className="mt-3 flex min-h-10 items-center justify-between gap-3 rounded-[9px] border border-neutral-200 bg-primary-soft/40 px-[13px] py-2.5 text-2xs text-neutral-500">{inspect?<><span>{inspect.label}</span><strong className="font-semibold text-neutral-900 tabular-nums">{inspect.text}</strong></>:<span>{s.inspect||String(year)}</span>}</div>}
  </div>;
}


// ── Timeline / Gantt ──────────────────────────────────────
export function TimelineChart({ data, months, height = 200, entrance = true }) {
  // Chart-level cycle (r16, migrated 2026-07-24): the row bars grow on
  // first load AND re-grow together on every data change, like the rest
  // of the bar family — the autonomous mount-once entrance left this
  // widget hard-swapping while its neighbours re-animated.
  const barsDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const w = 600, h = height, pad = { l: 100, r: 15, t: 28, b: 10 };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  const n = data.length, rowH = ch / n, totalMonths = months.length;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }}>
      {months.map((m, i) => (
        <g key={i}>
          <text x={pad.l + (i + 0.5) * (cw / totalMonths)} y={18} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{m}</text>
          <line x1={pad.l + i * (cw / totalMonths)} y1={pad.t} x2={pad.l + i * (cw / totalMonths)} y2={h - pad.b} stroke={NEUTRAL.track} strokeWidth="0.5" />
        </g>
      ))}
      {data.map((d, i) => {
        const y = pad.t + i * rowH;
        const x1 = pad.l + (d.start / totalMonths) * cw;
        const barW = ((d.end - d.start) / totalMonths) * cw;
        return (
          <g key={i} className="group">
            <line x1={pad.l} x2={w - pad.r} y1={y + rowH} y2={y + rowH} stroke={NEUTRAL.grid} strokeWidth="0.5" />
            <text x={pad.l - 6} y={y + rowH / 2 + 3} textAnchor="end" fontSize="11" fill={NEUTRAL.faint} fontWeight="500">{d.label}</text>
            {/* Entrance (ronda 12 sweep): the bars grow from their own
                start point (growX). Row labels/gridlines stay outside;
                the % label fades so the text never distorts. */}
            <EntranceGroup mode="growX" origin={`${x1}px 0px`} disabled={!entrance} drawn={barsDrawn}>
              <rect x={x1} y={y + rowH * 0.2} width={barW} height={rowH * 0.6} fill={PALETTE[i % PALETTE.length]} rx="4" opacity="0.35"
                className="transition-opacity group-hover:opacity-50" />
              {d.progress !== undefined && (
                <rect x={x1} y={y + rowH * 0.2} width={barW * d.progress} height={rowH * 0.6} fill={PALETTE[i % PALETTE.length]} rx="4"
                  className="transition-opacity group-hover:opacity-90" />
              )}
            </EntranceGroup>
            {/* Rule 1 ("somos BI"): bold progress label at the end of the bar */}
            {d.progress !== undefined && (
              <EntranceGroup mode="fade" disabled={!entrance} drawn={barsDrawn}>
                <text x={x1 + barW + 6} y={y + rowH / 2 + 3} fontSize="11" fontWeight="700" fill={PALETTE[i % PALETTE.length]}>
                  {Math.round(d.progress * 100)}%
                </text>
              </EntranceGroup>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ── Anomaly Detection Card ────────────────────────────────
export function AnomalyCard({ title, value, expected, severity = 'warning', description, timestamp, entrance = true, locale, strings, sentiment = 'neutral' }) {
  const s=requireStrings('AnomalyCard',strings,['expected']);
  const loc=requireLocale('AnomalyCard',locale);
  const delta=typeof value==='number'&&Number.isFinite(value)&&typeof expected==='number'&&Number.isFinite(expected)&&expected!==0?(value-expected)/Math.abs(expected)*100:null;
  const tone=severity==='critical'?'bad':severity==='warning'?'warn':'neutral';
  return <Callout tone={tone} accent={severity==='info'?'info':undefined} title={title} entrance={entrance} icon={<span>{severity==='info'?'i':'!'}</span>}>
    <div data-anomaly-values="" className="my-2 flex flex-wrap items-baseline gap-3"><strong className="font-display text-[28px] font-extrabold leading-[1.2] text-neutral-900 tabular-nums">{typeof value==='number'?value.toLocaleString(loc):value}</strong>
      {typeof expected==='number'&&<span>{s.expected} {expected.toLocaleString(loc)}</span>}
      {delta!=null&&<DeltaChip delta={delta} sentiment={sentiment} label={new Intl.NumberFormat(loc,{style:'percent',maximumFractionDigits:1,signDisplay:'exceptZero'}).format(delta/100)}/>}</div>
    {description&&<p>{description}</p>}{timestamp&&<p className="mt-2 text-xs">{timestamp}</p>}
  </Callout>;
}

// Composed from the same ring and meter primitives used in reports.
// A letter grade is authored explicitly, never inferred from an arbitrary score.
export function ScoreCard({title,score,maxScore=100,breakdown,color,entrance=true,grade}) {
  const componentTheme = useColorTheme('ScoreCard');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  return <ChartCard title={title}><div data-score-composition="" className="flex items-center gap-6 pt-4">
    <div className="shrink-0 text-center"><ProgressRing value={score} max={maxScore} size={90} strokeWidth={6} color={color} entrance={entrance}/>{grade&&<strong className="mt-1 block text-xs font-bold text-neutral-700">{grade}</strong>}</div>
    <div className="flex-1 space-y-3">{breakdown?.map(b=><MeterBar key={b.label} value={b.value} max={100} label={b.label} height={6} showValue valueLabel={b.value+' %'} ariaLabel={b.label+' '+b.value+' %'} hoverText={b.label+' · '+b.value+' %'}/>)}</div>
  </div></ChartCard>;
}

// ── Multi-Select Dropdown ─────────────────────────────────
export function MultiSelectDropdown({ options, selected, onToggle, label, placeholder = 'Select...' }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  useEffect(()=>{const outside=e=>{if(!root.current?.contains(e.target))setOpen(false)};document.addEventListener('mousedown',outside);return()=>document.removeEventListener('mousedown',outside)},[]);
  return (
    <div ref={root} className="relative" onKeyDown={e=>{if(e.key==="Escape"&&open){e.preventDefault();e.stopPropagation();setOpen(false);root.current?.querySelector("button")?.focus()}}}>
      {label && <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">{label}</p>}
      <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between h-8 rounded-lg border bg-white px-3 text-sm transition-all hover:shadow-sm focus-visible:ring-2 focus-visible:ring-accent/30 ${
          open ? 'border-accent ring-2 ring-accent/20' : 'border-neutral-200 text-neutral-700 hover:border-neutral-300'
        }`}>
        <span className="truncate">{selected.length ? `${selected.length} seleccionado${selected.length > 1 ? 's' : ''}` : placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SEMANTIC.muted} strokeWidth="2"
          className={`transition-transform shrink-0 ml-2 ${open ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
      </button>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selected.map(s => (
            <span key={s} className="inline-flex items-center gap-1 rounded-md bg-accent/10 text-accent-dark px-2 py-0.5 text-xs font-semibold">
              {s}
              <button onClick={() => onToggle(s)} className="hover:text-negative transition-colors ml-0.5">×</button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div data-dropdown-surface="list" className="absolute top-full left-0 mt-1 z-50 w-full max-h-48 overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-xl">
          {options.map(opt => {
            const isSelected = selected.includes(opt);
            return (
              <button key={opt} role="checkbox" aria-checked={isSelected} type="button" onClick={() => onToggle(opt)}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors ${
                  isSelected ? 'bg-accent/5 text-accent-dark font-semibold' : 'text-neutral-700 hover:bg-neutral-50'
                }`}>
                <span className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                  isSelected ? 'bg-accent border-accent' : 'border-neutral-300 group-hover:border-accent/50'
                }`}>
                  {isSelected && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                </span>
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Saved Filters / Presets ───────────────────────────────
export function SavedFilters({ presets, active, onSelect, label }) {
  return (
    <div>
      {label && <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">{label}</p>}
      <div className="flex flex-wrap gap-1.5">
        {presets.map(p => (
          <button key={p.key} onClick={() => onSelect(p.key)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${
              active === p.key
                ? 'bg-accent text-white border-accent shadow-sm'
                : 'bg-white text-neutral-600 border-neutral-200 hover:border-accent/40 hover:bg-accent/5 hover:text-accent-dark'
            }`}>
            {p.icon && <span className="text-xs">{p.icon}</span>}
            {p.label}
            {p.count !== undefined && (
              <span className={`text-2xs rounded-full px-1.5 py-0.5 font-bold ${active === p.key ? 'bg-white/25' : 'bg-neutral-100'}`}>{p.count}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
